/**
 * Search Page
 *
 * Full search screen for finding people and content tags.
 * Matches Flutter's search_screen.dart
 *
 * Architecture: Component → useSearch Hook → UseCases → Services
 *
 * Features:
 * - Pinned search bar with autofocus
 * - People section with "More people" pagination and collapse
 * - Content section with infinite scroll pagination via VirtualList onReachedEnd
 */

import { useCallback, useMemo } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { BackIconButton } from '@/presentation/components/common/BackIconButton';
import { SearchBar } from '@/presentation/components/common/SearchBar';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { SearchUserItem } from './components/SearchUserItem';
import { SearchContentTagItem } from './components/SearchContentTagItem';
import { useSearch } from './hooks/useSearch';
import { Navigator } from '@/presentation/routing/Navigator';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { ContentTagEntity } from '@/domain/entities/contentTag/contentTagEntity';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

const SEARCH_USERS_LIMIT = SERVER_FETCH_CONSTANTS.SEARCH_USERS_PAGINATION_LIMIT;

type SearchListItem =
  | { type: 'users-header' }
  | { type: 'user'; user: UserEntity }
  | { type: 'more-people' }
  | { type: 'tags-header' }
  | { type: 'tag'; tag: ContentTagEntity };

export const SearchPage = () => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const {
    keyword,
    users,
    contentTags,
    isSearching,
    hasMoreUsers,
    onSearchChanged,
    loadMoreUsers,
    collapseUsers,
    onScrollNearBottom,
  } = useSearch();

  useBasePage({
    onInit: () => {
      // Page mounted - search bar will autofocus
    },
  });

  const showMoreButton = !isSearching && (hasMoreUsers || users.length > SEARCH_USERS_LIMIT);

  const hasResults = users.length > 0 || contentTags.length > 0;
  const pageStatus = isSearching
    ? 'loading'
    : keyword.trim().length === 0
      ? 'idle'
      : hasResults
        ? 'results'
        : 'empty';

  const listItems = useMemo<SearchListItem[]>(() => {
    const items: SearchListItem[] = [];
    if (users.length > 0) {
      items.push({ type: 'users-header' });
      users.forEach(user => items.push({ type: 'user', user }));
      if (showMoreButton) items.push({ type: 'more-people' });
    }
    if (contentTags.length > 0) {
      items.push({ type: 'tags-header' });
      contentTags.forEach(tag => items.push({ type: 'tag', tag }));
    }
    return items;
  }, [users, contentTags, showMoreButton]);

  const getItemKey = useCallback((item: SearchListItem) => {
    switch (item.type) {
      case 'users-header': return 'users-header';
      case 'user': return `user-${item.user.firebaseId}`;
      case 'more-people': return 'more-people';
      case 'tags-header': return 'tags-header';
      case 'tag': return `tag-${item.tag.name}`;
    }
  }, []);

  const renderItem = (item: SearchListItem) => {
    switch (item.type) {
      case 'users-header':
        return (
          <p
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              fontSize: responsive.font.lg,
              margin: 0,
              paddingTop: responsive.spacing.lg,
              paddingBottom: responsive.spacing.base,
            }}
          >
            {l10n.search.people}
          </p>
        );

      case 'user':
        return <SearchUserItem user={item.user} />;

      case 'more-people':
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: responsive.avatar.medium }} />
            <div style={{ flex: 1, textAlign: 'center' }}>
              {hasMoreUsers && (
                <span
                  onClick={loadMoreUsers}
                  style={{
                    color: theme.text.secondary,
                    fontSize: responsive.font.sm,
                    cursor: 'pointer',
                  }}
                >
                  {l10n.search.morePeople}
                </span>
              )}
            </div>
            {users.length > SEARCH_USERS_LIMIT ? (
              <div
                onClick={collapseUsers}
                style={{
                  width: responsive.avatar.medium,
                  display: 'flex',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <AssetIcon
                  path="/assets/vectors/expand.svg"
                  width={responsive.icon.xs}
                  height={responsive.icon.xs}
                  color={theme.text.secondary}
                  rotationAngle={180}
                />
              </div>
            ) : (
              <div style={{ width: responsive.avatar.medium }} />
            )}
          </div>
        );

      case 'tags-header':
        return (
          <p
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              fontSize: responsive.font.lg,
              margin: 0,
              paddingTop: responsive.spacing.base,
              paddingBottom: responsive.spacing.base,
            }}
          >
            {l10n.search.content}
          </p>
        );

      case 'tag':
        return (
          <div
            onClick={() => item.tag.isHashtag === true ?
              Navigator.navigateToContentTag(item.tag.name) :
              Navigator.navigateToContentLabel(item.tag.name)}
            style={{ cursor: 'pointer' }}>
            <SearchContentTagItem contentTag={item.tag} />
          </div>
        );
    }
  };

  return (
    <div
      {...qaAttr({ id: 'search', status: pageStatus })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.background,
      }}
    >
      {/* Sticky header: back button + search bar */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          paddingTop: responsive.safeArea.top,
          paddingRight: responsive.spacing.lg,
          backgroundColor: theme.background,
        }}
      >
        <BackIconButton onClick={() => Navigator.goBack()} qa={{ id: 'search-back' }} />
        <div style={{ flex: 1 }}>
          <SearchBar
            value={keyword}
            onChange={onSearchChanged}
            placeholder={l10n.search.placeholder}
            qa={{ id: 'search-input' }}
          />
        </div>
      </div>

      {/* Results — VirtualList uses window scroll */}
      <div
        style={{
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
        }}
      >
        <VirtualList
          qa={{ id: 'search-results' }}
          items={listItems}
          renderItem={renderItem}
          getItemKey={getItemKey}
          defaultItemHeight={80}
          gap={0}
          onReachedEnd={onScrollNearBottom}
        />
      </div>
    </div>
  );
};
